/**
 * **P-140** — the board room, bound where the *source* lives instead of where the table lives.
 *
 * `views/shared/use-view-data.ts` has always re-read on `source.subscribe(...)`, and its comment
 * has always said "the board room's own change stream re-reads, exactly as the grid does". It
 * did not: `subscribe` is the `TrpcBoardDataSource`'s **own** emitter, which fires after each of
 * *its own* mutations, and the socket was joined in exactly one place —
 * `board-view/table/context.tsx` — a component only the table view mounts. So a kanban, a
 * calendar, a gantt, a timeline, a gallery or a workload repainted when it wrote and never when
 * anybody else did: two people on the same kanban, one drags a card, the other's board does not
 * move until a reload (round C3's `K2`).
 *
 * This is the missing half: one `useBoardRealtime` call, mounted once by the board screen for
 * every **non-table** view kind, which reduces the room's envelopes with the same
 * `lib/realtime/patches.ts` reducer the table uses and forwards the result into
 * `source.notify(...)` — so `use-view-data`'s existing subscription is fed by somebody else's
 * write as well as by its own, and no view kind has to know that a socket exists.
 *
 * ## Why one component in one place, and not a call inside `useViewData`
 *
 * `joinBoard` / `leaveBoard` are not reference-counted (`lib/realtime/socket.ts`): two hooks
 * joined to the same room and one of them unmounting would leave it for both. A view kind can
 * call `useViewData` twice (the kanban and the calendar both run a *probe* read for the board's
 * columns before the real one), so the binding cannot live in that hook. It lives beside the
 * source, mounted by the board screen exactly where the table's own `RealtimeBridge` is *not*:
 * the table keeps its store-level reconciliation (`isOwnMutation`, the stale-patch rule)
 * untouched, and the two are never mounted at the same time.
 *
 * ## What it does not do
 *
 * It does not reconcile: a non-table view holds no optimistic store to merge a patch into, it
 * re-reads. So every applicable patch collapses to "something under this board changed, of this
 * shape", which is exactly what `BoardChange` says, and the view's own `load()` is the merge.
 */
import { useTRPCClient } from '../../../lib/trpc';
import { useBoardRealtime } from '../../../lib/realtime/useBoardRealtime';
import type { StorePatch } from '../../../lib/realtime/patches';
import type { BoardChange, BoardDataSource, CellWrite } from './types';

/**
 * Which `BoardChange` a reduced socket patch is. Pure, so the mapping is a unit test rather
 * than a socket integration test; `null` is a patch a board view has nothing to do with (a
 * duplicate, a stale row, one of our own optimistic writes, an event nobody handles).
 */
export function boardChangeFor(patch: StorePatch): BoardChange | null {
  switch (patch.kind) {
    case 'ignored':
      return null;
    case 'cells':
      return {
        kind: 'cells',
        writes: patch.cells.map(
          (cell) => ({ itemId: cell.itemId, columnId: cell.columnId, value: cell.value }) as unknown as CellWrite,
        ),
      };
    // `refetch` is a truncated delta — "throw it away and read again", which for a view that
    // re-reads anyway is the same one re-read as any other item change.
    case 'item.created':
    case 'item.updated':
    case 'item.moved':
    case 'item.removed':
    case 'group.created':
    case 'group.updated':
    case 'group.removed':
    case 'refetch':
      return { kind: 'items' };
    case 'column.created':
    case 'column.updated':
    case 'column.removed':
      return { kind: 'columns' };
    /*
     * Deliberately **not** forwarded. A board view re-reads the board's *items*; the board row
     * and the view rows it also paints are TanStack caches that `useBoardChromeRealtime` already
     * invalidates for the header and the tab bar. Forwarding them would make this view's own
     * `views.update` — every settings toggle — come back over the socket as a board re-read,
     * which is a wasted round trip and, while a settings pane is open, a repaint underneath it.
     */
    case 'board.updated':
    case 'board.removed':
    case 'views.changed':
      return null;
    default:
      return null;
  }
}

export interface BoardViewRealtimeProps {
  source: BoardDataSource;
  boardId: string;
  viewId: string | null;
}

export function BoardViewRealtime({ source, boardId, viewId }: BoardViewRealtimeProps) {
  const client = useTRPCClient();
  const notify = source.notify?.bind(source);

  /*
   * The fixture source of `/__ui/*` answers every read from its seed and there is no api behind
   * it: a socket opened there is refused at the handshake and then reconnect-loops (E13, R1).
   * It also implements no `notify`, so there is nothing to feed either way.
   */
  const live = source.offline !== true && notify !== undefined;

  useBoardRealtime(
    live
      ? {
          boardId,
          viewId,
          applyRemotePatch: (patch) => {
            const change = boardChangeFor(patch);
            if (change !== null) notify(change);
          },
          // A non-table view paints no presence cursors and holds no store to revoke; a lost
          // room simply stops repainting, and the next read answers `forbidden` on its own.
          setPresence: () => undefined,
          setAccessRevoked: () => notify({ kind: 'view' }),
          reload: () => notify({ kind: 'items' }),
          // No optimistic store: our own write already re-read through `source.subscribe`, and
          // a second read of the same row is correct, only wasteful.
          isOwnPending: () => false,
          updatedAtOf: () => undefined,
          sync: async (since) => {
            const result = (await client.realtime.sync.query({ boardId, since })) as {
              now: string;
              truncated: boolean;
            };
            return result;
          },
        }
      : null,
  );

  // Nothing to paint: the room is the only thing this mounts.
  return null;
}
